/**
 * <waitlist-popup>
 *
 * Shared "Join Waitlist" popup. Extends the theme's BasicModal (centered modal
 * with body locking + open/close wiring via aria-controls). The button that
 * opens it supplies the Klaviyo list id (and optional heading) via data
 * attributes, so a single popup can serve many buttons/lists.
 *
 * On submit it subscribes the shopper to the Klaviyo list using the public
 * Client-Side Subscriptions API (only the public Site ID is exposed). If the
 * newsletter checkbox is ticked, a second subscription to the newsletter list
 * is made.
 */
if (!customElements.get('waitlist-popup')) {
  customElements.define(
    'waitlist-popup',
    class WaitlistPopup extends BasicModal {
      constructor() {
        super();
      }

      connectedCallback() {
        super.connectedCallback();

        // Cache element references and config once. Children move with the
        // element when it is re-parented to <body> on open, so this is stable.
        if (!this.form) {
          this.form = this.querySelector('.waitlist-popup__form');
          this.emailInput = this.querySelector('#WaitlistForm-email');
          this.newsletterInput = this.querySelector('#WaitlistForm-newsletter');
          this.submitButton = this.querySelector('.waitlist-popup__submit');
          this.headingEl = this.querySelector('[data-waitlist-heading]');
          this.errorEl = this.querySelector('[data-waitlist-error]');
          this.errorTextEl = this.querySelector('[data-waitlist-error-text]');
          this.successEl = this.querySelector('[data-waitlist-success]');

          this.companyId = this.dataset.companyId;
          this.newsletterListId = this.dataset.newsletterListId;
          this.defaultHeading = this.headingEl ? this.headingEl.textContent : '';

          // Each button must supply its own list id (no shared default); a button
          // without one can't subscribe and shows the "unconfigured" message.
          this.activeListId = '';
          this.activeSource = '';

          this.strings = {
            invalidEmail: 'Please enter a valid email address.',
            generic: 'Something went wrong. Please try again.',
            unconfigured: 'The waitlist is not available right now. Please try again later.',
          };
        }

        // Re-bind the submit handler on every connect using the current
        // AbortController signal (created in super.connectedCallback), so the
        // listener is cleaned up on disconnect and never duplicated.
        if (this.form) {
          this.form.addEventListener('submit', this.handleSubmit.bind(this), {
            signal: this.abortController.signal,
          });
        }
      }

      handleAfterShow() {
        super.handleAfterShow();
        this.resetMessages();

        const trigger = this.activeElement;
        if (trigger && trigger.dataset) {
          this.activeListId = trigger.dataset.waitlistListId || '';
          this.activeSource = trigger.dataset.waitlistSource || '';
          if (this.headingEl) {
            this.headingEl.textContent = trigger.dataset.waitlistHeading || this.defaultHeading;
          }
        }
      }

      async handleSubmit(event) {
        event.preventDefault();
        this.resetMessages();

        const email = (this.emailInput.value || '').trim();
        if (!this.validateEmail(email)) {
          this.showError(this.strings.invalidEmail);
          this.emailInput.focus();
          return;
        }

        if (!this.companyId || !this.activeListId) {
          this.showError(this.strings.unconfigured);
          return;
        }

        this.setBusy(true);
        try {
          await this.subscribe(email, this.activeListId, this.activeSource);

          if (this.newsletterInput && this.newsletterInput.checked && this.newsletterListId) {
            await this.subscribe(email, this.newsletterListId);
          }

          // Identify the profile too, mirroring the existing quiz form.
          if (window.klaviyo) {
            window.klaviyo.push(['identify', { $email: email }]);
          }

          this.showSuccess();
          this.form.reset();
        } catch (error) {
          console.error('[waitlist-popup]', error);
          this.showError(this.strings.generic);
        } finally {
          this.setBusy(false);
        }
      }

      subscribe(email, listId, source) {
        const profileAttributes = { email: email };
        if (source) {
          profileAttributes.properties = { 'Waitlisted Product': source };
        }

        const body = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Join Waitlist Popup',
              profile: {
                data: { type: 'profile', attributes: profileAttributes },
              },
            },
            relationships: {
              list: { data: { type: 'list', id: listId } },
            },
          },
        };

        return fetch(
          `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.companyId)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: '2024-10-15',
            },
            body: JSON.stringify(body),
          }
        ).then((response) => {
          if (!response.ok) {
            throw new Error(`Klaviyo subscription failed (${response.status})`);
          }
          return response;
        });
      }

      validateEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      }

      setBusy(isBusy) {
        if (!this.submitButton) return;
        this.submitButton.setAttribute('aria-busy', isBusy ? 'true' : 'false');
        this.submitButton.disabled = isBusy;
      }

      resetMessages() {
        if (this.errorEl) this.errorEl.classList.add('hidden');
        if (this.successEl) this.successEl.classList.add('hidden');
      }

      showError(message) {
        if (!this.errorEl) return;
        if (this.errorTextEl) this.errorTextEl.textContent = message;
        this.successEl && this.successEl.classList.add('hidden');
        this.errorEl.classList.remove('hidden');
      }

      showSuccess() {
        if (!this.successEl) return;
        this.errorEl && this.errorEl.classList.add('hidden');
        this.successEl.classList.remove('hidden');
      }
    }
  );
}
